import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { LapComparisonChannel, LapComparisonSeries } from "@/types";
import {
  COMPARISON_COLOR,
  REFERENCE_COLOR,
  formatSignedSeconds,
  nearestIndex,
  normalizeSelection,
  positionTickStep,
  valueDomain,
} from "./comparisonMath";

export interface ComparisonChartProps {
  series: LapComparisonSeries;
  /** Selected channels, in display order. */
  channels: LapComparisonChannel[];
  channelLabel: (channel: LapComparisonChannel) => string;
  deltaLabel: string;
  notRecordedLabel: string;
  cursorIndex: number | null;
  onCursorChange: (index: number | null) => void;
  selection: [number, number] | null;
  onSelectionChange: (selection: [number, number] | null) => void;
  /** id of the readout element that describes the cursor for AT users. */
  ariaDescribedBy?: string;
}

const MARGIN = { left: 64, right: 16, top: 6, bottom: 26 } as const;
const DELTA_LANE_HEIGHT = 120;
const CHANNEL_LANE_HEIGHT = 120;
const LANE_GAP = 8;
const DRAG_THRESHOLD_PX = 4;
const BACKGROUND = "#080810";
const LANE_BACKGROUND = "#0d0d16";
const GRID = "#1a1a2e";
const AXIS_TEXT = "#6b7280";
const LANE_TEXT = "#cbd5e1";
const DELTA_LINE = "#e5e7eb";
const DELTA_BEHIND_FILL = "rgba(248,113,113,0.22)";
const DELTA_AHEAD_FILL = "rgba(74,222,128,0.22)";
const CURSOR = "#e5e7eb";
const SELECTION_FILL = "rgba(96,165,250,0.12)";
const SELECTION_EDGE = "rgba(147,197,253,0.8)";

interface Lane {
  key: string;
  title: string;
  unit: string;
  top: number;
  height: number;
  domain: [number, number] | null;
  reference: (number | null)[];
  comparison: (number | null)[];
  kind: "delta" | "channel";
}

function formatTick(value: number, span: number): string {
  if (span >= 100) return value.toFixed(0);
  if (span >= 10) return value.toFixed(1);
  return value.toFixed(2);
}

export const ComparisonChart: React.FC<ComparisonChartProps> = ({
  series,
  channels,
  channelLabel,
  deltaLabel,
  notRecordedLabel,
  cursorIndex,
  onCursorChange,
  selection,
  onSelectionChange,
  ariaDescribedBy,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const baseRef = useRef<HTMLCanvasElement>(null);
  const overlayRef = useRef<HTMLCanvasElement>(null);
  const [width, setWidth] = useState(800);
  const dragRef = useRef<{
    pointerId: number;
    startX: number;
    startIndex: number;
    dragging: boolean;
  } | null>(null);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;
    const update = () => setWidth(Math.max(200, element.clientWidth));
    update();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const lanes = useMemo<Lane[]>(() => {
    const result: Lane[] = [];
    let top = MARGIN.top;
    result.push({
      key: "__delta",
      title: deltaLabel,
      unit: "s",
      top,
      height: DELTA_LANE_HEIGHT,
      domain: valueDomain([series.delta], { includeZero: true }),
      reference: series.delta,
      comparison: [],
      kind: "delta",
    });
    top += DELTA_LANE_HEIGHT + LANE_GAP;
    for (const channel of channels) {
      const values = series.values[channel.key];
      const reference = values?.reference ?? [];
      const comparison = values?.comparison ?? [];
      result.push({
        key: channel.key,
        title: channelLabel(channel),
        unit: channel.unit,
        top,
        height: CHANNEL_LANE_HEIGHT,
        domain: valueDomain([reference, comparison]),
        reference,
        comparison,
        kind: "channel",
      });
      top += CHANNEL_LANE_HEIGHT + LANE_GAP;
    }
    return result;
  }, [series, channels, channelLabel, deltaLabel]);

  const height =
    MARGIN.top +
    DELTA_LANE_HEIGHT +
    channels.length * (CHANNEL_LANE_HEIGHT + LANE_GAP) +
    MARGIN.bottom;
  const plotWidth = Math.max(10, width - MARGIN.left - MARGIN.right);
  const length = series.position[series.position.length - 1] ?? 0;

  const xScale = useCallback(
    (position: number) =>
      MARGIN.left + (length > 0 ? position / length : 0) * plotWidth,
    [length, plotWidth],
  );

  const yScale = useCallback((lane: Lane, value: number) => {
    const [min, max] = lane.domain ?? [0, 1];
    const normalized = (value - min) / (max - min || 1);
    return lane.top + lane.height - normalized * lane.height;
  }, []);

  const prepareCanvas = useCallback(
    (canvas: HTMLCanvasElement | null) => {
      if (!canvas) return null;
      const ctx = canvas.getContext("2d");
      if (!ctx) return null;
      const dpr = window.devicePixelRatio || 1;
      // Assigning width/height reallocates the bitmap; only do it when the
      // size changed so the overlay can repaint on every pointer move.
      if (canvas.width !== width * dpr || canvas.height !== height * dpr) {
        canvas.width = width * dpr;
        canvas.height = height * dpr;
        canvas.style.width = `${width}px`;
        canvas.style.height = `${height}px`;
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, width, height);
      return ctx;
    },
    [width, height],
  );

  const strokeSeries = useCallback(
    (
      ctx: CanvasRenderingContext2D,
      lane: Lane,
      values: (number | null)[],
      color: string,
      lineWidth = 1.6,
    ) => {
      ctx.strokeStyle = color;
      ctx.lineWidth = lineWidth;
      ctx.lineJoin = "round";
      ctx.beginPath();
      let pen = false;
      for (let i = 0; i < series.position.length; i++) {
        const v = values[i];
        if (v === null || v === undefined || !Number.isFinite(v)) {
          pen = false;
          continue;
        }
        const x = xScale(series.position[i]);
        const y = yScale(lane, v);
        if (!pen) {
          ctx.moveTo(x, y);
          pen = true;
        } else {
          ctx.lineTo(x, y);
        }
      }
      ctx.stroke();
    },
    [series.position, xScale, yScale],
  );

  // Base layer: lanes, grid, traces, axes.
  useEffect(() => {
    const ctx = prepareCanvas(baseRef.current);
    if (!ctx) return;
    ctx.fillStyle = BACKGROUND;
    ctx.fillRect(0, 0, width, height);

    for (const lane of lanes) {
      ctx.fillStyle = LANE_BACKGROUND;
      ctx.fillRect(MARGIN.left, lane.top, plotWidth, lane.height);

      if (lane.domain) {
        const [min, max] = lane.domain;
        const span = max - min;
        ctx.strokeStyle = GRID;
        ctx.lineWidth = 1;
        for (let g = 0; g <= 4; g++) {
          const y = lane.top + (lane.height * g) / 4;
          ctx.beginPath();
          ctx.moveTo(MARGIN.left, y);
          ctx.lineTo(MARGIN.left + plotWidth, y);
          ctx.stroke();
        }
        ctx.fillStyle = AXIS_TEXT;
        ctx.font = "10px ui-monospace, SFMono-Regular, Menlo, monospace";
        ctx.textAlign = "right";
        ctx.textBaseline = "top";
        ctx.fillText(formatTick(max, span), MARGIN.left - 6, lane.top + 2);
        ctx.textBaseline = "bottom";
        ctx.fillText(formatTick(min, span), MARGIN.left - 6, lane.top + lane.height - 2);
      }

      if (lane.kind === "delta" && lane.domain) {
        const zeroY = yScale(lane, 0);
        // Tinted area between the delta trace and zero: red when the
        // comparison lap is behind, green when ahead.
        for (const sign of [1, -1] as const) {
          ctx.fillStyle = sign > 0 ? DELTA_BEHIND_FILL : DELTA_AHEAD_FILL;
          ctx.beginPath();
          let open = false;
          for (let i = 0; i < series.position.length; i++) {
            const v = series.delta[i] ?? 0;
            const clipped = sign > 0 ? Math.max(0, v) : Math.min(0, v);
            const x = xScale(series.position[i]);
            const y = yScale(lane, clipped);
            if (!open) {
              ctx.moveTo(x, zeroY);
              open = true;
            }
            ctx.lineTo(x, y);
          }
          if (open) {
            ctx.lineTo(xScale(length), zeroY);
            ctx.closePath();
            ctx.fill();
          }
        }
        ctx.setLineDash([4, 4]);
        ctx.strokeStyle = "#4b5563";
        ctx.beginPath();
        ctx.moveTo(MARGIN.left, zeroY);
        ctx.lineTo(MARGIN.left + plotWidth, zeroY);
        ctx.stroke();
        ctx.setLineDash([]);
        strokeSeries(ctx, lane, series.delta, DELTA_LINE, 1.1);
      } else if (lane.kind === "channel") {
        if (lane.domain) {
          strokeSeries(ctx, lane, lane.reference, REFERENCE_COLOR);
          strokeSeries(ctx, lane, lane.comparison, COMPARISON_COLOR);
        } else {
          ctx.fillStyle = AXIS_TEXT;
          ctx.font = "11px system-ui, sans-serif";
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.fillText(
            notRecordedLabel,
            MARGIN.left + plotWidth / 2,
            lane.top + lane.height / 2,
          );
        }
      }

      ctx.fillStyle = LANE_TEXT;
      ctx.font = "600 11px system-ui, sans-serif";
      ctx.textAlign = "left";
      ctx.textBaseline = "top";
      ctx.fillText(
        lane.unit ? `${lane.title} · ${lane.unit}` : lane.title,
        MARGIN.left + 8,
        lane.top + 6,
      );
    }

    // Position axis.
    const step = positionTickStep(length);
    ctx.fillStyle = AXIS_TEXT;
    ctx.font = "10px ui-monospace, SFMono-Regular, Menlo, monospace";
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    const axisY = height - MARGIN.bottom + 6;
    for (let p = 0; p <= length; p += step) {
      const x = xScale(p);
      ctx.strokeStyle = GRID;
      ctx.beginPath();
      ctx.moveTo(x, MARGIN.top);
      ctx.lineTo(x, height - MARGIN.bottom);
      ctx.stroke();
      // Labels near the right edge are right-aligned so they stay inside
      // the plot instead of running into the margin.
      ctx.textAlign = x > MARGIN.left + plotWidth - 24 ? "right" : "center";
      ctx.fillText(`${p} m`, x, axisY);
    }
  }, [
    prepareCanvas,
    lanes,
    width,
    height,
    plotWidth,
    length,
    series,
    xScale,
    yScale,
    strokeSeries,
    notRecordedLabel,
  ]);

  // Overlay layer: selection, cursor and cursor values.
  useEffect(() => {
    const ctx = prepareCanvas(overlayRef.current);
    if (!ctx) return;

    if (selection) {
      const [a, b] = normalizeSelection(selection[0], selection[1]);
      const x0 = xScale(series.position[a] ?? 0);
      const x1 = xScale(series.position[b] ?? 0);
      ctx.fillStyle = SELECTION_FILL;
      ctx.fillRect(x0, MARGIN.top, Math.max(1, x1 - x0), height - MARGIN.top - MARGIN.bottom);
      ctx.strokeStyle = SELECTION_EDGE;
      ctx.lineWidth = 1;
      for (const x of [x0, x1]) {
        ctx.beginPath();
        ctx.moveTo(x, MARGIN.top);
        ctx.lineTo(x, height - MARGIN.bottom);
        ctx.stroke();
      }
    }

    if (cursorIndex !== null && cursorIndex >= 0 && cursorIndex < series.position.length) {
      const x = xScale(series.position[cursorIndex]);
      ctx.strokeStyle = CURSOR;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(x, MARGIN.top);
      ctx.lineTo(x, height - MARGIN.bottom);
      ctx.stroke();

      ctx.font = "600 11px ui-monospace, SFMono-Regular, Menlo, monospace";
      ctx.textBaseline = "top";
      ctx.textAlign = "right";
      for (const lane of lanes) {
        const right = MARGIN.left + plotWidth - 8;
        if (lane.kind === "delta") {
          const d = series.delta[cursorIndex] ?? 0;
          const label = formatSignedSeconds(d);
          ctx.fillStyle = label.startsWith("+")
            ? "#fca5a5"
            : label.startsWith("−")
              ? "#86efac"
              : LANE_TEXT;
          ctx.fillText(label, right, lane.top + 6);
          continue;
        }
        const rv = lane.reference[cursorIndex];
        const cv = lane.comparison[cursorIndex];
        const fmt = (v: number | null | undefined) =>
          v === null || v === undefined ? "—" : v.toFixed(Math.abs(v) >= 100 ? 0 : 1);
        ctx.fillStyle = COMPARISON_COLOR;
        ctx.fillText(fmt(cv), right, lane.top + 6);
        ctx.fillStyle = REFERENCE_COLOR;
        ctx.fillText(fmt(rv), right - 64, lane.top + 6);
        for (const [value, color] of [
          [rv, REFERENCE_COLOR],
          [cv, COMPARISON_COLOR],
        ] as const) {
          if (value === null || value === undefined) continue;
          ctx.beginPath();
          ctx.arc(x, yScale(lane, value), 3.5, 0, Math.PI * 2);
          ctx.fillStyle = color;
          ctx.fill();
        }
      }
    }
  }, [prepareCanvas, selection, cursorIndex, series, xScale, yScale, lanes, plotWidth, height]);

  const indexFromClientX = useCallback(
    (clientX: number) => {
      const rect = overlayRef.current?.getBoundingClientRect();
      if (!rect) return null;
      const x = clientX - rect.left;
      const ratio = (x - MARGIN.left) / plotWidth;
      const position = Math.min(1, Math.max(0, ratio)) * length;
      return nearestIndex(series.position, position);
    },
    [plotWidth, length, series.position],
  );

  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLCanvasElement>) => {
      const index = indexFromClientX(event.clientX);
      if (index === null) return;
      // Capture only once a horizontal drag is recognised so a vertical
      // swipe on touch devices still scrolls the page (touch-action: pan-y).
      dragRef.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startIndex: index,
        dragging: false,
      };
    },
    [indexFromClientX],
  );

  const handlePointerMove = useCallback(
    (event: React.PointerEvent<HTMLCanvasElement>) => {
      const index = indexFromClientX(event.clientX);
      if (index === null) return;
      onCursorChange(index);
      const drag = dragRef.current;
      if (!drag || drag.pointerId !== event.pointerId) return;
      if (!drag.dragging) {
        if (Math.abs(event.clientX - drag.startX) < DRAG_THRESHOLD_PX) {
          return;
        }
        drag.dragging = true;
        event.currentTarget.setPointerCapture?.(event.pointerId);
      }
      onSelectionChange([drag.startIndex, index]);
    },
    [indexFromClientX, onCursorChange, onSelectionChange],
  );

  const releaseDrag = useCallback((event: React.PointerEvent<HTMLCanvasElement>) => {
    const drag = dragRef.current;
    dragRef.current = null;
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    return drag;
  }, []);

  const handlePointerUp = useCallback(
    (event: React.PointerEvent<HTMLCanvasElement>) => {
      const drag = releaseDrag(event);
      if (drag && !drag.dragging) {
        // A plain click clears the section selection.
        onSelectionChange(null);
      }
    },
    [releaseDrag, onSelectionChange],
  );

  // A cancelled gesture (browser took over for scrolling) keeps whatever
  // section was already measured.
  const handlePointerCancel = useCallback(
    (event: React.PointerEvent<HTMLCanvasElement>) => {
      releaseDrag(event);
    },
    [releaseDrag],
  );

  const handlePointerLeave = useCallback(() => {
    if (!dragRef.current) onCursorChange(null);
  }, [onCursorChange]);

  // Keyboard: arrows move the cursor (Shift ±10 points), Shift+Arrow with
  // a selection anchor extends the section, Home/End jump, Escape clears.
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLCanvasElement>) => {
      const last = series.position.length - 1;
      if (last < 0) return;
      const current = cursorIndex ?? (selection ? selection[1] : 0);
      let next: number | null = null;
      switch (event.key) {
        case "ArrowLeft":
          next = Math.max(0, current - (event.shiftKey ? 10 : 1));
          break;
        case "ArrowRight":
          next = Math.min(last, current + (event.shiftKey ? 10 : 1));
          break;
        case "Home":
          next = 0;
          break;
        case "End":
          next = last;
          break;
        case "Escape":
          onSelectionChange(null);
          event.preventDefault();
          return;
        default:
          return;
      }
      event.preventDefault();
      onCursorChange(next);
      if (event.shiftKey && (event.key === "ArrowLeft" || event.key === "ArrowRight")) {
        onSelectionChange([selection ? selection[0] : current, next]);
      }
    },
    [series.position.length, cursorIndex, selection, onCursorChange, onSelectionChange],
  );

  return (
    <div ref={containerRef} className="relative w-full" style={{ height }}>
      <canvas ref={baseRef} className="absolute inset-0" aria-hidden="true" />
      <canvas
        ref={overlayRef}
        className="absolute inset-0 cursor-crosshair focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4b6bfb]"
        style={{ touchAction: "pan-y" }}
        role="img"
        tabIndex={0}
        aria-label={deltaLabel}
        aria-describedby={ariaDescribedBy}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerCancel}
        onPointerLeave={handlePointerLeave}
        onKeyDown={handleKeyDown}
      />
    </div>
  );
};

export default ComparisonChart;
